import { apiFetch } from '../lib/apiFetch';

/**
 * react-query cache identity for the workshop-type catalogue (BON-391). Every
 * reader MUST use these so the Settings list and the dashboards' type pickers
 * share one cache entry — otherwise a type edited in Settings stays stale in
 * the create drawer for a full `staleTime`. Writers invalidate the key.
 */
export const WORKSHOP_TYPES_QUERY_KEY = ['workshop-types'] as const;
export const WORKSHOP_TYPES_STALE_MS = 300_000;

/**
 * WorkshopType — the honest ops DTO (BON-117 / ADR-0014). Mirrors
 * backend/src/services/workshop-type.service.ts `WorkshopTypeDTO`. The old
 * warehouse-shaped fields (dollar-string `defaultPrice`, `regionPrices`,
 * `_count`, `treeSpecies.commonName`) are gone — price is integer cents,
 * species is exposed via `treeSpecies.name`, and `workshopCount` powers the
 * delete guard + usage display.
 */
export interface WorkshopType {
  id: string;
  name: string;
  displayName: string | null;
  description: string | null;
  defaultDurationMinutes: number | null;
  /** Default price in integer cents (nullable). */
  defaultPriceCents: number | null;
  defaultCapacity: number | null;
  coveredByTreeGuarantee: boolean;
  treeSpeciesId: string | null;
  treeSpecies: { id: string; name: string } | null;
  isActive: boolean;
  /** Number of workshops referencing this type — guards delete (a type in use
   *  cannot be hard-deleted; deactivate instead). */
  workshopCount: number;
  createdAt: string;
  updatedAt: string;
}

/** Write payload for create/update. `coveredByTreeGuarantee` + `isActive` are
 *  required on CREATE (server schema); the partial PATCH accepts any subset. */
export interface WorkshopTypeInput {
  name: string;
  displayName: string | null;
  description: string | null;
  defaultDurationMinutes: number | null;
  defaultPriceCents: number | null;
  defaultCapacity: number | null;
  coveredByTreeGuarantee: boolean;
  treeSpeciesId: string | null;
  isActive: boolean;
}

/** Thrown on a 4xx carrying a `fieldErrors` map (e.g. duplicate name 409,
 *  bad species FK 400) or a plain guarded-delete 409 ("in use") message. */
export class WorkshopTypeError extends Error {
  fieldErrors?: Record<string, string>;
  status: number;
  constructor(message: string, status: number, fieldErrors?: Record<string, string>) {
    super(message);
    this.name = 'WorkshopTypeError';
    this.status = status;
    this.fieldErrors = fieldErrors;
  }
}

async function throwError(res: Response, fallback: string): Promise<never> {
  const data = (await res.json().catch(() => ({}))) as {
    message?: string;
    error?: string;
    fieldErrors?: Record<string, string>;
  };
  throw new WorkshopTypeError(data.message || data.error || fallback, res.status, data.fieldErrors);
}

export async function fetchWorkshopTypes(): Promise<WorkshopType[]> {
  const res = await apiFetch('/api/v1/workshop-types');
  if (!res.ok) throw new Error('Failed to fetch workshop types');
  const data = (await res.json()) as { workshopTypes: WorkshopType[] };
  return data.workshopTypes;
}

export async function fetchWorkshopType(id: string): Promise<WorkshopType> {
  const res = await apiFetch(`/api/v1/workshop-types/${id}`);
  if (!res.ok) await throwError(res, 'Failed to fetch workshop type');
  const data = (await res.json()) as { workshopType: WorkshopType };
  return data.workshopType;
}

export async function createWorkshopType(input: WorkshopTypeInput): Promise<WorkshopType> {
  const res = await apiFetch('/api/v1/workshop-types', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  if (!res.ok) await throwError(res, 'Failed to create workshop type');
  const data = (await res.json()) as { workshopType: WorkshopType };
  return data.workshopType;
}

export async function updateWorkshopType(
  id: string,
  patch: Partial<WorkshopTypeInput>,
): Promise<WorkshopType> {
  const res = await apiFetch(`/api/v1/workshop-types/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  if (!res.ok) await throwError(res, 'Failed to update workshop type');
  const data = (await res.json()) as { workshopType: WorkshopType };
  return data.workshopType;
}

/** DELETE /api/v1/workshop-types/:id — guarded hard-delete. A type referenced
 *  by any workshop returns 409 with an "in use … deactivate instead" message,
 *  surfaced to the caller via {@link WorkshopTypeError}. */
export async function deleteWorkshopType(id: string): Promise<void> {
  const res = await apiFetch(`/api/v1/workshop-types/${id}`, { method: 'DELETE' });
  if (!res.ok) await throwError(res, 'Failed to delete workshop type');
}
